/**
 * Themed empty state (Fate & Tide art, 2026-10-21): art + title + line + optional call to action, for a
 * list that has nothing in it at all. A list FILTERED to nothing keeps its plain "No results" line —
 * the art means "nothing here yet", not "your search missed". The one exception is general search
 * (general-search/index.ts): that page IS the query and has no dataset of its own to be empty, and the owner's
 * scope named "no search results" as an art surface, so a miss there gets the nets.
 *
 * Pages opt a list mount in with attributes, read by emptyStateFromAttributes():
 *   data-ft-empty-art="nets|chest|loom|pool|orb"   required to opt in
 *   data-ft-empty-title="…"                    heading (falls back to data-ft-empty-text)
 *   data-ft-empty-text="…"                     the line under it (existing attribute, still the plain fallback)
 *   data-ft-empty-cta-label="…" data-ft-empty-cta-href="…"   optional button
 * Art lives on the CDN at images/site/empty/<art>-{560,1040}.webp. Styles: css/src/99g-empty-state.css.
 */

export const EMPTY_STATE_ARTS = ['nets', 'chest', 'loom', 'pool', 'orb'] as const;
export type EmptyStateArt = typeof EMPTY_STATE_ARTS[number];

export interface EmptyStateSpec {
    art: EmptyStateArt;
    /** Small plain-words line above the title, for art that could be misread (the orb is not a loader). */
    label?: string;
    title: string;
    text?: string;
    action?: { label: string; href: string };
    /** Smaller art for tight panes (a sheet tab, a side panel). */
    compact?: boolean;
}

const ART_BASE = 'https://cdn.fatetide.com/images/site/empty/';

export function buildEmptyState(spec: EmptyStateSpec): HTMLElement {
    const box = document.createElement('div');
    box.className = spec.compact ? 'ft-empty ft-empty--compact' : 'ft-empty';
    box.setAttribute('role', 'status');
    box.setAttribute('data-ft-empty-state', spec.art);

    const img = document.createElement('img');
    img.className = 'ft-empty__art';
    img.src = `${ART_BASE}${spec.art}-560.webp`;
    img.srcset = `${ART_BASE}${spec.art}-560.webp 560w, ${ART_BASE}${spec.art}-1040.webp 1040w`;
    img.sizes = spec.compact ? '220px' : '(max-width: 600px) 90vw, 420px';
    img.width = 560;
    img.height = 383;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';

    const title = document.createElement('p');
    title.className = 'ft-empty__title';
    title.textContent = spec.title;
    box.append(img);
    if (spec.label) {
        const label = document.createElement('p');
        label.className = 'ft-empty__label';
        label.textContent = spec.label;
        box.append(label);
    }
    box.append(title);

    if (spec.text) {
        const text = document.createElement('p');
        text.className = 'ft-empty__text';
        text.textContent = spec.text;
        box.append(text);
    }
    if (spec.action) {
        const link = document.createElement('a');
        link.className = 'btn btn-warning ft-empty__action';
        link.href = spec.action.href;
        link.textContent = spec.action.label;
        box.append(link);
    }
    return box;
}

/** The opt-in spec a list mount declares, or null when the page did not opt in (keep the plain line). */
export function emptyStateFromAttributes(root: Element, fallbackText: string): EmptyStateSpec | null {
    const art = root.getAttribute('data-ft-empty-art') ?? '';
    if (!(EMPTY_STATE_ARTS as readonly string[]).includes(art)) return null;
    const text = root.getAttribute('data-ft-empty-text') ?? '';
    const title = root.getAttribute('data-ft-empty-title') || text || fallbackText;
    const label = root.getAttribute('data-ft-empty-cta-label') ?? '';
    const href = root.getAttribute('data-ft-empty-cta-href') ?? '';
    return {
        art: art as EmptyStateArt,
        title,
        text: title !== text && text !== '' ? text : undefined,
        action: label && href ? { label, href } : undefined,
    };
}
